import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { AlertTriangle, Clock, DollarSign, Shield, RefreshCw, CheckCircle2, AlertCircle, ShieldAlert, Zap, Search, FileWarning, ArrowDownCircle, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  CRITICAL: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", badge: "bg-red-100 text-red-700", dot: "bg-red-500" },
  HIGH: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700", badge: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  MEDIUM: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  LOW: { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600", badge: "bg-gray-100 text-gray-600", dot: "bg-gray-400" },
};

const TYPE_ICONS = {
  stuck_commitment: DollarSign,
  stuck_fund_subscription: DollarSign,
  reconciliation_mismatch: FileWarning,
  stale_maker_checker: Shield,
  failed_distribution: AlertTriangle,
  distribution_failure: AlertTriangle,
  capital_operation_failure: AlertTriangle,
  allocation_failure: ArrowDownCircle,
  security_denial: ShieldAlert,
  idempotency_stale: Zap,
  failed_background_job: Zap,
};

export default function ExceptionQueuePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconLoading, setReconLoading] = useState(false);
  const [resolving, setResolving] = useState(null);
  const [resolveNotes, setResolveNotes] = useState("");
  const [reconHistory, setReconHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchExceptions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/exceptions/`, { headers });
      setData(res.data);
    } catch { setData({ exceptions: [], total: 0, summary: {} }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchExceptions(); }, [fetchExceptions]);

  const triggerReconciliation = async () => {
    setReconLoading(true);
    try {
      await axios.post(`${API}/admin/exceptions/reconcile`, {}, { headers });
      await fetchExceptions();
    } catch (e) { console.error("Reconciliation failed:", e); }
    finally { setReconLoading(false); }
  };

  const fetchReconHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/exceptions/reconciliation-history`, { headers });
      setReconHistory(res.data.reports);
      setShowHistory(true);
    } catch { setReconHistory([]); }
  };

  const resolveException = async (excId, action) => {
    try {
      await axios.post(`${API}/admin/exceptions/resolve/${excId}`, { notes: resolveNotes, action }, { headers });
      setResolving(null);
      setResolveNotes("");
      await fetchExceptions();
    } catch (e) { console.error("Resolution failed:", e); }
  };

  if (loading) return <div className="text-center py-12 text-sm text-[#999]" data-testid="exception-loading">Loading exceptions...</div>;

  const { exceptions = [], summary = {}, last_reconciliation_status, last_reconciliation } = data || {};

  return (
    <div className="space-y-6" data-testid="exception-queue-panel">
      {/* Summary Bar */}
      <div className="rounded-2xl border border-[#e8e0d0] bg-white/80 backdrop-blur-sm p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-sm font-bold text-[#222] uppercase tracking-wider" data-testid="exception-queue-title">Exception Queue</h3>
            <p className="text-xs text-[#888] mt-1">Financial anomalies, reconciliation mismatches, and operational exceptions</p>
          </div>
          <div className="flex items-center gap-2">
            {summary.critical > 0 && <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold" data-testid="critical-count">{summary.critical} CRITICAL</span>}
            {summary.high > 0 && <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold" data-testid="high-count">{summary.high} HIGH</span>}
            {summary.medium > 0 && <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold" data-testid="medium-count">{summary.medium} MEDIUM</span>}
            {exceptions.length === 0 && <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-testid="all-clear">ALL CLEAR</span>}
            <button onClick={fetchExceptions} className="p-1.5 rounded-lg border border-[#e8e0d0] hover:bg-[#faf7f2] transition-colors" data-testid="refresh-exceptions">
              <RefreshCw className="h-3.5 w-3.5 text-[#666]" />
            </button>
          </div>
        </div>

        {/* Reconciliation Controls */}
        <div className="mt-4 pt-4 border-t border-[#e8e0d0] flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-[11px] text-[#888]">
            {last_reconciliation_status ? (
              last_reconciliation_status === "CLEAN" ? (
                <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Last reconciliation: <span className="font-medium text-emerald-600">CLEAN</span></>
              ) : (
                <><AlertCircle className="h-3.5 w-3.5 text-amber-500" /> Last reconciliation: <span className="font-medium text-amber-600">{last_reconciliation_status}</span></>
              )
            ) : (
              <><Clock className="h-3.5 w-3.5" /> No reconciliation run yet</>
            )}
            {last_reconciliation && <span className="text-[10px] text-[#bbb] ml-1">({last_reconciliation.slice(0, 16).replace("T", " ")})</span>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchReconHistory}
              className="px-3 py-1.5 rounded-lg border border-[#e8e0d0] text-[10px] font-medium text-[#666] hover:bg-[#faf7f2] transition-colors"
              data-testid="view-recon-history">
              <Clock className="h-3 w-3 inline mr-1" /> History
            </button>
            <button onClick={triggerReconciliation} disabled={reconLoading}
              className="px-3 py-1.5 rounded-lg bg-[#1a3329] text-white text-[10px] font-medium hover:bg-[#2a4d3e] transition-colors disabled:opacity-50"
              data-testid="trigger-reconciliation">
              {reconLoading ? "Running..." : "Run Reconciliation"}
            </button>
          </div>
        </div>
      </div>

      {/* Reconciliation History */}
      {showHistory && reconHistory && (
        <div className="rounded-2xl border border-[#e8e0d0] bg-white/80 p-5">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold text-[#222] uppercase tracking-wider">Reconciliation History</h4>
            <button onClick={() => setShowHistory(false)} className="text-[10px] text-[#888] hover:text-[#333]">Close</button>
          </div>
          {reconHistory.length === 0 ? (
            <p className="text-xs text-[#999]">No reconciliation reports yet</p>
          ) : (
            <div className="space-y-2">
              {reconHistory.map((r, i) => (
                <div key={r.report_id || i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${r.status === "CLEAN" ? "bg-emerald-50" : "bg-amber-50"}`}>
                  <div className="flex items-center gap-2">
                    {r.status === "CLEAN" ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-500" />}
                    <span className="text-[11px] font-medium">{r.status}</span>
                    <span className="text-[10px] text-[#888]">{r.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <span className="text-[10px] text-[#888]">{r.issues_found || 0} issue(s)</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Exception Items */}
      {exceptions.length === 0 ? (
        <div className="rounded-2xl border border-[#e8e0d0] bg-white/80 p-8 text-center" data-testid="no-exceptions">
          <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
          <p className="text-sm font-medium text-[#333]">No exceptions found</p>
          <p className="text-xs text-[#888] mt-1">All operations are running normally</p>
        </div>
      ) : (
        <div className="space-y-2">
          {exceptions.map((ex, i) => {
            const style = SEVERITY_STYLES[ex.severity] || SEVERITY_STYLES.MEDIUM;
            const Icon = TYPE_ICONS[ex.type] || AlertTriangle;
            const isResolving = resolving === (ex.exception_id || `${ex.type}-${ex.entity_id}-${i}`);
            const exKey = ex.exception_id || `${ex.type}-${ex.entity_id}-${i}`;
            return (
              <motion.div key={exKey} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.02 }}>
                <div className={`rounded-xl border ${style.border} ${style.bg} p-4`} data-testid={`exception-${ex.type}-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg border ${style.border} flex-shrink-0 mt-0.5`}>
                      <Icon className={`h-4 w-4 ${style.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold ${style.text}`}>{ex.title}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${style.badge}`}>{ex.severity}</span>
                        <span className="text-[8px] text-[#bbb] uppercase tracking-wide">{ex.type?.replace(/_/g, " ")}</span>
                      </div>
                      <p className="text-[11px] text-[#666] mt-0.5">{ex.detail}</p>
                      {ex.drift_cents != null && ex.drift_cents !== 0 && (
                        <p className="text-[10px] text-red-600 mt-0.5 font-medium">Drift: ${(Math.abs(ex.drift_cents) / 100).toFixed(2)}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1.5">
                        {ex.entity_id && <span className="text-[9px] text-[#bbb] font-mono">{ex.entity_id.slice(0, 12)}...</span>}
                        {ex.created_at && <span className="text-[9px] text-[#bbb]">{ex.created_at.slice(0, 16).replace("T", " ")}</span>}
                      </div>
                    </div>
                    {ex.exception_id && (
                      <div className="flex-shrink-0 flex gap-1">
                        {!isResolving ? (
                          <button onClick={() => setResolving(exKey)}
                            className={`px-2.5 py-1 rounded-lg border ${style.border} text-[10px] font-medium ${style.text} hover:bg-white/60 transition-colors`}
                            data-testid={`resolve-btn-${i}`}>
                            Resolve
                          </button>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            <input value={resolveNotes} onChange={e => setResolveNotes(e.target.value)}
                              placeholder="Resolution notes..."
                              className="text-[10px] px-2 py-1 border rounded-lg w-48" data-testid={`resolve-notes-${i}`} />
                            <div className="flex gap-1">
                              <button onClick={() => resolveException(ex.exception_id, "resolved")}
                                className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[9px] font-medium" data-testid={`confirm-resolve-${i}`}>Resolve</button>
                              <button onClick={() => resolveException(ex.exception_id, "investigating")}
                                className="px-2 py-0.5 rounded bg-blue-600 text-white text-[9px] font-medium" data-testid={`investigate-${i}`}>Investigate</button>
                              <button onClick={() => resolveException(ex.exception_id, "dismissed")}
                                className="px-2 py-0.5 rounded bg-gray-400 text-white text-[9px] font-medium" data-testid={`dismiss-${i}`}>Dismiss</button>
                              <button onClick={() => { setResolving(null); setResolveNotes(""); }}
                                className="px-2 py-0.5 rounded border text-[9px]">Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
